import { Entity } from '../../Entity';
/**
 * 实体构建器 - 提供流式API创建和配置实体
 */
export class EntityBuilder {
    constructor(scene, storageManager) {
        this.scene = scene;
        this.storageManager = storageManager;
        this.entity = new Entity("", scene.identifierPool.checkOut());
    }
    /**
     * 设置实体名称
     * @param name 实体名称
     * @returns 实体构建器
     */
    named(name) {
        this.entity.name = name;
        return this;
    }
    /**
     * 设置实体标签
     * @param tag 标签
     * @returns 实体构建器
     */
    tagged(tag) {
        this.entity.tag = tag;
        return this;
    }
    /**
     * 添加组件
     * @param component 组件实例
     * @returns 实体构建器
     */
    with(component) {
        this.entity.addComponent(component);
        return this;
    }
    /**
     * 添加多个组件
     * @param components 组件数组
     * @returns 实体构建器
     */
    withComponents(...components) {
        for (const component of components) {
            this.entity.addComponent(component);
        }
        return this;
    }
    /**
     * 条件性添加组件
     * @param condition 条件
     * @param component 组件实例
     * @returns 实体构建器
     */
    withIf(condition, component) {
        if (condition) {
            this.entity.addComponent(component);
        }
        return this;
    }
    /**
     * 使用工厂函数创建并添加组件
     * @param factory 组件工厂函数
     * @returns 实体构建器
     */
    withFactory(factory) {
        const component = factory();
        this.entity.addComponent(component);
        return this;
    }
    /**
     * 配置组件属性
     * @param componentType 组件类型
     * @param configurator 配置函数
     * @returns 实体构建器
     */
    configure(componentType, configurator) {
        const component = this.entity.getComponent(componentType);
        if (component) {
            configurator(component);
        }
        return this;
    }
    /**
     * 设置实体为启用状态
     * @param enabled 是否启用
     * @returns 实体构建器
     */
    enabled(enabled = true) {
        this.entity.enabled = enabled;
        return this;
    }
    /**
     * 设置实体为活跃状态
     * @param active 是否活跃
     * @returns 实体构建器
     */
    active(active = true) {
        this.entity.active = active;
        return this;
    }
    /**
     * 添加子实体
     * @param childBuilder 子实体构建器
     * @returns 实体构建器
     */
    withChild(childBuilder) {
        const child = childBuilder.build();
        this.entity.addChild(child);
        return this;
    }
    /**
     * 批量添加子实体
     * @param childBuilders 子实体构建器数组
     * @returns 实体构建器
     */
    withChildren(...childBuilders) {
        for (const childBuilder of childBuilders) {
            const child = childBuilder.build();
            this.entity.addChild(child);
        }
        return this;
    }
    /**
     * 使用工厂函数创建子实体
     * @param childFactory 子实体工厂函数
     * @returns 实体构建器
     */
    withChildFactory(childFactory) {
        const childBuilder = childFactory(this.entity);
        const child = childBuilder.build();
        this.entity.addChild(child);
        return this;
    }
    /**
     * 条件性添加子实体
     * @param condition 条件
     * @param childBuilder 子实体构建器
     * @returns 实体构建器
     */
    withChildIf(condition, childBuilder) {
        if (condition) {
            const child = childBuilder.build();
            this.entity.addChild(child);
        }
        return this;
    }
    /**
     * 构建并返回实体
     * @returns 构建的实体
     */
    build() {
        return this.entity;
    }
    /**
     * 构建实体并添加到场景
     * @returns 构建的实体
     */
    spawn() {
        this.scene.addEntity(this.entity);
        return this.entity;
    }
    /**
     * 克隆当前构建器
     * @returns 新的实体构建器
     */
    clone() {
        const newBuilder = new EntityBuilder(this.scene, this.storageManager);
        // 这里需要深度克隆实体，简化实现
        newBuilder.entity = this.entity; // 实际应该是深度克隆
        return newBuilder;
    }
}
//# sourceMappingURL=EntityBuilder.js.map